import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"

export interface ListItemValueProps {
	value: string
}

/**
 * Renders `value` as a single line of text, truncated with an ellipsis if it overflows,
 * styled with `semibold` weight to match `List.Item`'s default value styling. Sets the `title`
 * attribute to `value` so the full text is available as a tooltip on hover, unless `value` is
 * the `emptyPlaceholder` (e.g. "–"), in which case a tooltip wouldn't add any value.
 *
 * Use this as `List.ItemValue` when a `List.Item`'s value is placed alongside other elements
 * in a `Flex` (e.g. a color tile or an edit button) so only the text itself is truncated
 * instead of the whole row, which is what `List.Item`'s own `valueSingleLine` would do.
 *
 * For plain string values, prefer passing the string directly as `List.Item`'s `value`
 * prop instead, which already truncates it out of the box.
 */
export const ListItemValue = styled(({ value, ...rest }: ListItemValueProps) => (
	// ...rest contains className, which is crucial for the styles to be applied
	<PText ellipsis weight="semibold" title={value === emptyPlaceholder ? undefined : value} {...rest}>
		{value}
	</PText>
))`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`
